import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { filter } from 'rxjs/operators';
import { Chart, registerables } from 'chart.js';
import {
  BarChartData,
  fontFamily,
  getBarChartColor,
  getBarChartConfig,
} from '../../../../../../shared/helper/charthelper';
import { ReportingService } from '../../../../../../shared/store/reporting/reporting.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, debounceTime, Observable } from 'rxjs';
import {
  GroupedLabelWithValue,
  InputReporting,
  LabelWithValue,
} from '../../../../../../shared/store/reporting/types';
import { SearchService } from '../../../../../../shared/store/search/search.service';
import { Filter } from '../../../../../../shared/store/search/types';
import { ViewService } from '../../../../../../shared/store/view/views.service';
import { View } from '../../../../../../shared/store/view/types';

@UntilDestroy()
@Component({
  selector: 'app-sales-person-production-dashboard-chart',
  standalone: false,
  templateUrl: './sales-person-production-dashboard-chart.component.html',
  styleUrl: './sales-person-production-dashboard-chart.component.scss',
})
export class SalesPersonProductionDashboardChartComponent
  implements OnInit, OnDestroy
{
  //title
  titleDailySalesChart = signal('');
  titleMonthlySalesChart = signal('');
  titleYearlySalesChart = signal('');

  //charts
  private monthlyProgressChart: any;
  private dailySalesChart: any;
  private monthlySalesChart: any;
  private yearlySalesChart: any;

  view$: Observable<View>;
  reportMonthlyProgressData$: Observable<GroupedLabelWithValue[]>;
  reportSalesChartTotalAndObjectivesData$: Observable<GroupedLabelWithValue[]>;

  constructor(
    private readonly reportingService: ReportingService,
    private readonly searchService: SearchService,
    private readonly viewService: ViewService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.reportMonthlyProgressData$ =
      this.reportingService.getReportMonthlyProgress.value$;
    this.reportSalesChartTotalAndObjectivesData$ =
      this.reportingService.getReportSalesTotalAndObjectives.value$;
  }

  ngOnInit(): void {
    Chart.register(...registerables);

    this.reportSalesChartTotalAndObjectivesData$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        this.buildDailySalesChart(result, fontFamily);
        this.buildMonthlySalesChart(result, fontFamily);
        this.buildYearlySalesChart(result, fontFamily);
      });

    combineLatest([this.view$, this.searchService.getActiveFilters()])
      .pipe(
        filter(([view, activeFilter]) => !!view && !!activeFilter),
        debounceTime(200),
        untilDestroyed(this),
      )
      .subscribe(([view, activeFilters]) => {
        this.buildMonthlyProgressChart(activeFilters, fontFamily);

        let inputReport: InputReporting = {
          parameters: ['getSalesChart'],
          query: new Map<string, string>(),
          filters: activeFilters,
        };

        this.reportingService.getReportSalesTotalAndObjectives.call(
          inputReport,
        );
      });
  }

  private buildMonthlyProgressChart(
    activesFilters: Filter[],
    fontFamily: string,
  ) {
    let inputReport: InputReporting = {
      parameters: ['progress', 'monthly'],
      query: new Map<string, string>(),
      filters: activesFilters,
    };
    this.reportingService.getReportMonthlyProgress.call(inputReport);

    this.reportMonthlyProgressData$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        const chartData = this.buildBarChartData(result, true);
        const maxStack = Math.max(
          ...chartData.datasets.flatMap((ds) => ds.data),
        );
        if (this.monthlyProgressChart) {
          this.monthlyProgressChart.clear();
          this.monthlyProgressChart.data = chartData;
          this.monthlyProgressChart.options = getBarChartConfig(
            chartData,
            fontFamily,
            maxStack,
          ).options;
          this.monthlyProgressChart.update();
        } else {
          this.monthlyProgressChart = new Chart(
            'monthlyProgressChart',
            getBarChartConfig(chartData, fontFamily, maxStack),
          );
        }
      });
  }

  private buildDailySalesChart(
    data: GroupedLabelWithValue[],
    fontFamily: string,
  ) {
    const chartData = this.buildBarChartData(data, true, 'daily');
    const maxStack = Math.max(...chartData.datasets.flatMap((ds) => ds.data));
    if (this.dailySalesChart) {
      this.dailySalesChart.clear();
      this.dailySalesChart.data = chartData;
      this.dailySalesChart.options = getBarChartConfig(
        chartData,
        fontFamily,
        maxStack,
        false,
        true,
      ).options;
      this.dailySalesChart.update();
    } else {
      this.dailySalesChart = new Chart(
        'dailySalesChart',
        getBarChartConfig(chartData, fontFamily, maxStack, false, true),
      );
    }
  }

  private buildMonthlySalesChart(
    data: GroupedLabelWithValue[],
    fontFamily: string,
  ) {
    const chartData = this.buildBarChartData(data, true, 'monthly');
    const maxStack = Math.max(...chartData.datasets.flatMap((ds) => ds.data));
    if (this.monthlySalesChart) {
      this.monthlySalesChart.clear();
      this.monthlySalesChart.data = chartData;
      this.monthlySalesChart.options = getBarChartConfig(
        chartData,
        fontFamily,
        maxStack,
        false,
        true,
      ).options;
      this.monthlySalesChart.update();
    } else {
      this.monthlySalesChart = new Chart(
        'monthlySalesChart',
        getBarChartConfig(chartData, fontFamily, maxStack, false, true),
      );
    }
  }

  private buildYearlySalesChart(
    data: GroupedLabelWithValue[],
    fontFamily: string,
  ) {
    const chartData = this.buildBarChartData(data, true, 'yearly');
    const maxStack = Math.max(...chartData.datasets.flatMap((ds) => ds.data));
    if (this.yearlySalesChart) {
      this.yearlySalesChart.clear();
      this.yearlySalesChart.data = chartData;
      this.yearlySalesChart.options = getBarChartConfig(
        chartData,
        fontFamily,
        maxStack,
        false,
        true,
      ).options;
      this.yearlySalesChart.update();
    } else {
      this.yearlySalesChart = new Chart(
        'yearlySalesChart',
        getBarChartConfig(chartData, fontFamily, maxStack, false, true),
      );
    }
  }

  private buildBarChartData(
    result: GroupedLabelWithValue[],
    xLabel: boolean,
    type?: 'daily' | 'monthly' | 'yearly',
  ): BarChartData {
    const labels = xLabel
      ? result[0].values
          .filter((x) => (type && x.label.startsWith(type)) || (!type && true))
          .map((labelWithValue: LabelWithValue) =>
            !type
              ? labelWithValue.label
              : labelWithValue.label.slice(
                  labelWithValue.label.indexOf('/') + 1,
                ),
          )
      : [''];
    const datasets = result.map((group, index) => ({
      label: group.groupLabel,
      data: group.values
        .filter((x) => (type && x.label.startsWith(type)) || (!type && true))
        .map((labelWithValue: any) => labelWithValue.value),
      backgroundColor: getBarChartColor(group.groupColor),
      barPercentage: 1,
    }));

    if (type) {
      const titles = result[0].values
        .filter((x) => type && x.label.startsWith(type))
        .map((labelWithValue: LabelWithValue) =>
          labelWithValue.label.slice(labelWithValue.label.indexOf('/') + 1),
        );

      switch (type) {
        case 'yearly':
          this.titleYearlySalesChart.set(titles[0]);
          break;
        case 'monthly':
          this.titleMonthlySalesChart.set(titles[0]);
          break;
        case 'daily':
          this.titleDailySalesChart.set(titles[0]);
          break;
        default:
          break;
      }
    }

    return <BarChartData>{ labels, datasets };
  }

  ngOnDestroy(): void {
    this.reportingService.getReportMonthlyProgress.reset();
    this.reportingService.getReportSalesTotalAndObjectives.reset();
  }
}
